<template>
  <div class="reference-tag-chip-selector">
    <reference-tag-chip
      v-for="tag in sortedTags"
      :key="tag.id"
      :label="tag.label"
      :color="getChipColor(tag)"
      :text-color="getChipTextColor(tag)"
      :small="small"
      @click="toggleTag(tag)"
    />
  </div>
</template>

<script>
import { Tag } from '@/models';
import { typeHelpers } from '@/modules';
import ReferenceTagChip from './ReferenceTagChip';

export default {
  name: 'ReferenceTagChipSelector',
  components: {
    ReferenceTagChip,
  },
  props: {
    value: {
      type: Array,
      required: true,
      validator: (tags) => typeHelpers.isArrayOfType(tags, Tag),
    },
    availableTags: {
      type: Array,
      required: true,
      validator: (tags) => typeHelpers.isArrayOfType(tags, Tag),
    },
    small: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['input'],
  data: () => ({
    currentTagState: {},
  }),
  computed: {
    sortedTags() {
      const copy = [...this.availableTags];
      return copy.sort((a, b) => {
        // Tags loaded via a slim fragment can be missing `createdAt`
        // (null); guard so one missing date can't crash the whole
        // reference detail view. Undated tags sort last.
        if (!a.createdAt && !b.createdAt) return 0;
        if (!a.createdAt) return 1;
        if (!b.createdAt) return -1;
        return a.createdAt.diff(b.createdAt);
      });
    },
    initialTagState() {
      const states = Object.fromEntries(
        this.availableTags.map((tag) => [tag.id, false])
      );
      for (const tag of this.value) {
        states[tag.id] = true;
      }
      return states;
    },
  },
  watch: {
    value: {
      handler(tags) {
        const states = Object.fromEntries(
          tags.map((tag) => [tag.id, false])
        );
        for (const tag of this.value) {
          states[tag.id] = true;
        }
        this.currentTagState = states;
      },
      immediate: true,
    },
  },
  methods: {
    toggleTag(tag) {
      this.$set(
        this.currentTagState,
        tag.id,
        !this.currentTagState[tag.id]
      );
      this.emitInput();
    },
    emitInput() {
      const tags = this.availableTags.filter(
        (tag) => this.currentTagState[tag.id]
      );
      this.$emit('input', tags);
    },
    getChipColor(tag) {
      return this.currentTagState[tag.id]
        ? 'primary'
        : 'blue lighten-4';
    },
    getChipTextColor(tag) {
      return !this.currentTagState[tag.id] ? 'primary' : undefined;
    },
  },
};
</script>
<style scoped>
.reference-tag-chip-selector {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}
</style>
